import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { logger, scopedStorage, resolveAppUrl } from '@lark-apaas/client-toolkit-lite';
import { toast } from 'sonner';

/**
 * 版本号检测 + 自动刷新组件
 *
 * 背景：
 * - 微信 X5 WebView 对 HTML 的缓存极顽固，即使服务端下发了 Cache-Control，
 *   微信的预加载机制 / 离线包也可能把旧 HTML 缓存数天甚至更久
 * - 用户清缓存太麻烦，且不可控
 *
 * 方案：
 * 1. 每次构建生成唯一版本号（时间戳 + 随机串），注入到：
 *    - index.html 的 <meta name="app-build-version"> 里
 *    - JS 代码的 import.meta.env.APP_BUILD_VERSION 里
 * 2. 应用启动后，fetch 当前页面 HTML（加 ?v= 时间戳绕过缓存），
 *    用正则解析出 meta 里的最新版本号
 * 3. 对比当前运行版本 vs 最新版本：
 *    - 一致 → 什么也不做
 *    - 不一致 → 说明本地加载的是旧 HTML → 弹 toast + 悬浮刷新按钮
 * 4. 用户点击刷新 → 加 ?v=xxx 参数绕过缓存 reload
 * 5. 防死循环：reload 最多执行 1 次（通过 scopedStorage 标记）
 *
 * 为什么不用 version.json：
 * - 平台版（doubaoapps 域名）的静态资源上传路径受平台管控，
 *   额外加的 json 文件不一定能正确映射到访问路径
 * - fetch 首页 HTML 更可靠：HTML 一定存在，且 meta 里版本号是构建时注入的
 */

const STORAGE_KEY = 'app_version_reload_count';

// 从构建注入的版本号（Vite define）
const CURRENT_VERSION = import.meta.env.APP_BUILD_VERSION || 'dev';

// 从 HTML 字符串中解析 app-build-version meta 的值
function extractVersionFromHtml(html: string): string | null {
  const match = html.match(/<meta[^>]+name=["']app-build-version["'][^>]+content=["']([^"']+)["']/i);
  if (match) return match[1];
  // 兼容 content 在 name 前面的写法
  const match2 = html.match(/<meta[^>]+content=["']([^"']+)["'][^>]+name=["']app-build-version["']/i);
  return match2 ? match2[1] : null;
}

export default function VersionChecker() {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [latestVersion, setLatestVersion] = useState('');

  const handleRefresh = useCallback(() => {
    // 标记已刷新过，防死循环
    try {
      scopedStorage.setItem(STORAGE_KEY, '1');
    } catch {
      sessionStorage.setItem(STORAGE_KEY, '1');
    }

    // 加 v 参数绕过缓存，强制加载最新版
    const url = new URL(window.location.href);
    url.searchParams.set('v', Date.now().toString());
    window.location.replace(url.toString());
  }, []);

  useEffect(() => {
    // 开发模式跳过
    if (import.meta.env.DEV) return;

    // 已经刷新过一次了，不再检测（防死循环）
    try {
      const reloaded = scopedStorage.getItem(STORAGE_KEY);
      if (reloaded === '1') {
        logger.info('[version-check] already reloaded once, skip check');
        return;
      }
    } catch {
      const reloaded = sessionStorage.getItem(STORAGE_KEY);
      if (reloaded === '1') return;
    }

    // 当前版本无效，跳过
    if (!CURRENT_VERSION || CURRENT_VERSION === 'dev') return;

    let cancelled = false;

    async function checkVersion() {
      try {
        // fetch 当前页面 HTML，加时间戳绕过 CDN / 微信缓存
        // 用 cache: 'no-store' 强制走网络
        const checkUrl = resolveAppUrl(`/?vcheck=${Date.now()}`);
        const res = await fetch(checkUrl, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
          credentials: 'same-origin',
        });

        if (!res.ok) {
          logger.warn('[version-check] html fetch failed:', res.status);
          return;
        }

        const html = await res.text();
        const remoteVersion = extractVersionFromHtml(html);

        if (!remoteVersion) {
          logger.warn('[version-check] remote version not found in html');
          return;
        }

        if (cancelled) return;

        if (remoteVersion !== CURRENT_VERSION) {
          logger.info(
            '[version-check] new version found! current=',
            CURRENT_VERSION,
            'latest=',
            remoteVersion,
          );
          setLatestVersion(remoteVersion);
          setHasNewVersion(true);

          // 弹 toast 提示用户
          toast('检测到新版本', {
            description: '点击左上角刷新按钮更新到最新版',
            action: {
              label: '立即刷新',
              onClick: handleRefresh,
            },
            duration: 15000,
          });
        } else {
          logger.info('[version-check] version is up to date:', CURRENT_VERSION);
        }
      } catch (err) {
        logger.warn('[version-check] check failed:', String(err));
      }
    }

    // 延迟 3 秒检测，不阻塞首屏渲染（给微信 WebView 足够时间完成首屏）
    const timer = setTimeout(checkVersion, 3000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [handleRefresh]);

  // 没有新版本 → 什么也不渲染
  if (!hasNewVersion) return null;

  return (
    <button
      onClick={handleRefresh}
      className="fixed top-3 left-3 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium shadow-lg shadow-primary/30 hover:bg-primary/90 active:scale-95 transition-all"
      title={`新版本：${latestVersion.slice(0, 12)}`}
    >
      <RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '2s' }} />
      <span>有新版本</span>
    </button>
  );
}
